/**
 * Opções de limite de tolerância de um agente. O mesmo agente pode ter limites de normas
 * diferentes, e a norma de origem de cada um é obrigatória. Sem a permissão de atualizar, a
 * seção só lista.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Straighten as LimitIcon } from '@mui/icons-material';
import { SectionCard } from '../../../components';
import {
  hazardousAgentService,
  type HazardousAgentToleranceLimit,
} from '../../../services/hazardousAgentService';
import { serverMessage } from '../errorMessage';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';

interface Props {
  agentId: number;
  limits: HazardousAgentToleranceLimit[];
  canEdit: boolean;
  onChanged: () => void;
}

const describe = (limit: HazardousAgentToleranceLimit): string =>
  [limit.toleranceLimit, limit.measurementUnit].filter(Boolean).join(' ');

interface LimitDialogProps {
  agentId: number;
  limit: HazardousAgentToleranceLimit | null;
  onClose: () => void;
  onSaved: () => void;
}

const LimitDialog: React.FC<LimitDialogProps> = ({ agentId, limit, onClose, onSaved }) => {
  const [value, setValue] = useState(limit?.toleranceLimit ?? '');
  const [unit, setUnit] = useState(limit?.measurementUnit ?? '');
  const [source, setSource] = useState(limit?.source ?? '');
  const [errors, setErrors] = useState<{ value?: string; source?: string }>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const found: { value?: string; source?: string } = {};
    if (!value.trim()) found.value = 'Informe o valor do limite.';
    if (!source.trim()) found.source = 'Informe a norma de origem (por exemplo, NR-15 ou ACGIH).';
    setErrors(found);
    if (found.value || found.source) return;

    const input = {
      toleranceLimit: value.trim(),
      measurementUnit: unit.trim() || null,
      source: source.trim(),
    };
    try {
      setSaving(true);
      setServerError(null);
      if (limit) await hazardousAgentService.updateLimit(agentId, limit.id, input);
      else await hazardousAgentService.createLimit(agentId, input);
      onSaved();
    } catch (err: unknown) {
      setServerError(serverMessage(err, 'Não foi possível salvar a opção. Tente de novo.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{limit ? 'Editar opção de limite' : 'Nova opção de limite'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {serverError && <Alert severity="error">{serverError}</Alert>}
          <TextField
            label="Valor do limite"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={Boolean(errors.value)}
            helperText={errors.value ?? 'Como está na norma, por exemplo: 78'}
            required
            fullWidth
          />
          <TextField
            label="Unidade (se tiver)"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            helperText="Por exemplo: ppm ou mg/m³"
            fullWidth
          />
          <TextField
            label="Norma de origem"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            error={Boolean(errors.source)}
            helperText={errors.source ?? 'De onde vem este limite, por exemplo: NR-15'}
            required
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          Salvar opção
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ToleranceLimitsSection: React.FC<Props> = ({ agentId, limits, canEdit, onChanged }) => {
  const [editing, setEditing] = useState<HazardousAgentToleranceLimit | null>(null);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<HazardousAgentToleranceLimit | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  const closeDialog = () => {
    setAdding(false);
    setEditing(null);
  };

  const saved = () => {
    closeDialog();
    onChanged();
  };

  const remove = async () => {
    if (!removing) return;
    try {
      setRemoveBusy(true);
      setRemoveError(null);
      await hazardousAgentService.deleteLimit(agentId, removing.id);
      setRemoving(null);
      onChanged();
    } catch (err: unknown) {
      setRemoveError(serverMessage(err, 'Não foi possível remover a opção. Tente de novo.'));
    } finally {
      setRemoveBusy(false);
    }
  };

  return (
    <SectionCard
      title="Opções de limite de tolerância"
      icon={<LimitIcon />}
      badge={<Chip size="small" label={limits.length} />}
      action={
        canEdit ? (
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
            Adicionar opção
          </Button>
        ) : undefined
      }
    >
      {limits.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Este agente ainda não tem opção de limite.
          {canEdit ? ' Use "Adicionar opção" para cadastrar a primeira.' : ''}
        </Typography>
      ) : (
        <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />} spacing={1.5}>
          {limits.map((limit) => (
            <Box key={limit.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body1" fontWeight="medium">
                    {describe(limit)}
                  </Typography>
                  {limit.usadoNosDocumentos && (
                    <Chip size="small" color="info" variant="outlined" label="Usado nos documentos" />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {`Norma: ${limit.source ?? '—'}`}
                </Typography>
              </Box>
              {canEdit && (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    aria-label={`Editar opção ${describe(limit)}`}
                    onClick={() => setEditing(limit)}
                  >
                    Editar
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    aria-label={`Remover opção ${describe(limit)}`}
                    onClick={() => {
                      setRemoveError(null);
                      setRemoving(limit);
                    }}
                  >
                    Remover
                  </Button>
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {(adding || editing) && (
        <LimitDialog agentId={agentId} limit={editing} onClose={closeDialog} onSaved={saved} />
      )}

      <ConfirmDialog
        open={Boolean(removing)}
        title="Remover esta opção de limite?"
        message={`A opção ${removing ? describe(removing) : ''} deixa de ser oferecida para este agente.${
          removing?.usadoNosDocumentos
            ? ' Ela é a que os documentos (LTCAT e PGR) usam hoje: depois de remover, eles passam a usar a opção seguinte.'
            : ''
        }`}
        confirmLabel="Sim, remover"
        confirmColor="error"
        loading={removeBusy}
        error={removeError}
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </SectionCard>
  );
};

export default ToleranceLimitsSection;
